import React from 'react';
import { useParams } from 'react-router-dom';
import styles from './Team.module.scss';
import Table from '../../../Common/Table/Table/Table';
import useApiData from '../../../../../hooks/useApiData';
import Loading from '../../../Common/Loading/Loading';
import { getErrorComponentFromHttpError } from '../../../Common/Error/Error';
import ScoreDisplay from '../../../Common/ScoreDisplay/ScoreDisplay';
import { v4 as uuid } from 'uuid';

function teamMemberSortFn(a, b) {
  if (a.isLead && b.isLead) {
    if (a.fullName < b.fullName) {
      return -1;
    }
    if (a.fullName > b.fullName) {
      return 1;
    }
    return 0;
  }
  if (a.isLead) {
    return -1;
  }
  if (b.isLead) {
    return 1;
  }
  if (a.fullName < b.fullName) {
    return -1;
  }
  if (a.fullName > b.fullName) {
    return 1;
  }
  return 0;
}

export default function Team() {
  const { teamId } = useParams();
  const { data, error, isLoading } = useApiData({
    path: `/user/teams/${teamId}`,
    params: {
      includeTeamMembers: true,
      includeGoals: true,
    },
  });

  if (isLoading) {
    return <Loading message="Fetching team..." />;
  }

  if (error || data === null) {
    return getErrorComponentFromHttpError(error);
  }

  const userData = data.teamMembers.map((tm) => ({
    ...tm,
    fullName: `${tm.firstName} ${tm.lastName}`,
    performanceScore: (
      <ScoreDisplay
        subjectType="teamMember"
        scoreType="performance"
        teamId={teamId}
        userId={tm.cognitoId}
        fromDate={new Date(new Date().getTime() - 6 * 7 * 24 * 3600 * 1000).toISOString().substring(0, 10)}
        toDate={new Date().toISOString().substring(0, 10)}
      />
    ),
    processAdoptionScore: (
      <ScoreDisplay
        subjectType="teamMember"
        scoreType="processAdoption"
        teamId={teamId}
        userId={tm.cognitoId}
        fromDate={new Date(new Date().getTime() - 7 * 24 * 3600 * 1000).toISOString().substring(0, 10)}
        toDate={new Date().toISOString().substring(0, 10)}
      />
    ),
  }));

  userData.sort(teamMemberSortFn);

  return (
    <div className={styles.team}>
      <h1>Team: {data.label}</h1>
      <div className={styles.cards}>
        <div className={styles.card}>
          <div className={styles.row}>
            <div className={styles.stack}>
              <div className={styles.score}>
                <ScoreDisplay scoreType="performance" subjectType="team" teamId={teamId} grow />
              </div>
              <h3>All time</h3>
            </div>
            <div className={styles.stack}>
              <div className={styles.score}>
                <ScoreDisplay
                  scoreType="performance"
                  subjectType="team"
                  teamId={teamId}
                  fromDate={new Date(new Date().getTime() - 6 * 7 * 24 * 3600 * 1000).toISOString().substring(0, 10)}
                  toDate={new Date().toISOString().substring(0, 10)}
                  grow
                />
              </div>
              <h3>Past 6 weeks</h3>
            </div>
            <div className={styles.stack}>
              <div className={styles.score}>
                <ScoreDisplay
                  scoreType="performance"
                  subjectType="team"
                  teamId={teamId}
                  fromDate={new Date(new Date().getTime() - 7 * 24 * 3600 * 1000).toISOString().substring(0, 10)}
                  toDate={new Date().toISOString().substring(0, 10)}
                  grow
                />
              </div>
              <h3>Past week</h3>
            </div>
          </div>
          <h2>Team Performance Score</h2>
        </div>
        <div className={styles.card}>
          <div className={styles.row}>
            <div className={styles.stack}>
              <div className={styles.score}>
                <ScoreDisplay scoreType="processAdoption" subjectType="team" teamId={teamId} grow />
              </div>
              <h3>All time</h3>
            </div>
            <div className={styles.stack}>
              <div className={styles.score}>
                <ScoreDisplay
                  scoreType="processAdoption"
                  subjectType="team"
                  teamId={teamId}
                  fromDate={new Date(new Date().getTime() - 6 * 7 * 24 * 3600 * 1000).toISOString().substring(0, 10)}
                  toDate={new Date().toISOString().substring(0, 10)}
                  grow
                />
              </div>
              <h3>Past 6 weeks</h3>
            </div>
            <div className={styles.stack}>
              <div className={styles.score}>
                <ScoreDisplay
                  scoreType="processAdoption"
                  subjectType="team"
                  teamId={teamId}
                  fromDate={new Date(new Date().getTime() - 7 * 24 * 3600 * 1000).toISOString().substring(0, 10)}
                  toDate={new Date().toISOString().substring(0, 10)}
                  grow
                />
              </div>
              <h3>Past week</h3>
            </div>
          </div>
          <h2>Team Process Adoption Score</h2>
        </div>
      </div>
      <div className={styles.cards}>
        <div className={styles.card}>
          <div className={styles.row}>
            <div className={styles.stack}>
              <div className={styles.score}>
                <ScoreDisplay id={uuid()} subjectType="organisation" scoreType="performance" grow />
              </div>
              <h3>All time</h3>
            </div>
            <div className={styles.stack}>
              <div className={styles.score}>
                <ScoreDisplay
                  subjectType="organisation"
                  scoreType="performance"
                  fromDate={new Date(new Date().getTime() - 6 * 7 * 24 * 3600 * 1000).toISOString().substring(0, 10)}
                  toDate={new Date().toISOString().substring(0, 10)}
                  grow
                />
              </div>
              <h3>Past 6 weeks</h3>
            </div>
            <div className={styles.stack}>
              <div className={styles.score}>
                <ScoreDisplay
                  subjectType="organisation"
                  scoreType="performance"
                  fromDate={new Date(new Date().getTime() - 7 * 24 * 3600 * 1000).toISOString().substring(0, 10)}
                  toDate={new Date().toISOString().substring(0, 10)}
                  grow
                />
              </div>
              <h3>Past week</h3>
            </div>
          </div>
          <h2>Organisation Performance Score</h2>
        </div>
        <div className={styles.card}>
          <div className={styles.row}>
            <div className={styles.stack}>
              <div className={styles.score}>
                <ScoreDisplay scoreType="processAdoption" subjectType="organisation" grow />
              </div>
              <h3>All time</h3>
            </div>
            <div className={styles.stack}>
              <div className={styles.score}>
                <ScoreDisplay
                  scoreType="processAdoption"
                  subjectType="organisation"
                  fromDate={new Date(new Date().getTime() - 6 * 7 * 24 * 3600 * 1000).toISOString().substring(0, 10)}
                  toDate={new Date().toISOString().substring(0, 10)}
                  grow
                />
              </div>
              <h3>Past 6 weeks</h3>
            </div>
            <div className={styles.stack}>
              <div className={styles.score}>
                <ScoreDisplay
                  scoreType="processAdoption"
                  subjectType="organisation"
                  fromDate={new Date(new Date().getTime() - 7 * 24 * 3600 * 1000).toISOString().substring(0, 10)}
                  toDate={new Date().toISOString().substring(0, 10)}
                  grow
                />
              </div>
              <h3>Past week</h3>
            </div>
          </div>
          <h2>Organisation Process Adoption Score</h2>
        </div>
      </div>
      <Table
        rows={userData}
        keyColumn="cognitoId"
        allowSort
        linkFunction={(id) => `teamMembers/${id}`}
        title={`Team members`}
        rowLinks={{
          fullName: {
            label: 'View User',
            to: (id) => `/administrator/users/${id}`,
          },
        }}
        columns={[
          {
            id: 'firstName',
            label: 'First Name',
            numeric: false,
          },
          {
            id: 'lastName',
            label: 'Last Name',
            numeric: false,
          },
          {
            id: 'isLead',
            label: 'Team Leader',
            boolean: true,
          },
          {
            id: 'performanceScore',
            label: 'Performance (past 6 weeks)',
            component: true,
          },
          {
            id: 'processAdoptionScore',
            label: 'Process Adoption (past week)',
            component: true,
          },
        ]}
      />
    </div>
  );
}
